import { FC, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar, VariantType } from 'notistack'
import { useHotkeys } from 'react-hotkeys-hook'
import { Key } from 'ts-key-enum'

import { Box, Typography, Link as MuiLink } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'

import { ApplicationState } from 'store'
import { Notification, removeSnackbar } from 'store/notifier'
import { useTheme } from '@mui/styles'
import { getIconColor } from 'common/notifier/helper'
import { SELECTED_THEME } from 'common/api/storageConsts'

let displayed: string[] = []

const useStyles = makeStyles((theme) => ({
  notifier: {
    maxHeight: '30vh',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '50vw',
    paddingLeft: '1rem !important',
    '& #notistack-snackbar': {
      maxHeight: '30vh',
      width: '100%'
    },
    '& .SnackbarItem-message': {
      '& svg': {
        marginLeft: '.5rem'
      }
    }
  },
  sucessNotifier: {
    '& .SnackbarItem-message': {
      borderLeft: `3px solid ${theme.palette.successCard.contrastText}`
    }
  },
  infoNotifier: {
    '& .SnackbarItem-message': {
      borderLeft: `3px solid ${theme.palette.infoCard.contrastText}`
    }
  },
  errorNotifier: {
    '& .SnackbarItem-message': {
      borderLeft: `3px solid ${theme.palette.errorCard.contrastText}`
    }
  },
  warningNotifier: {
    '& .SnackbarItem-message': {
      borderLeft: `3px solid ${theme.palette.warningCard.contrastText}`
    }
  },
  defaultNotifier: {
    '& .SnackbarItem-message': {
      borderLeft: 'none'
    }
  },
  message: {
    maxHeight: '10vh',
    wordBreak: 'break-word',
    whiteSpace: 'pre-line'
  },
  title: {
    whiteSpace: 'pre-line',
    wordBreak: 'break-word',
    fontWeight: 'bold'
  },
  contentWrapper: {
    maxHeight: '20vh',
    overflow: 'auto',
    width: 'calc(100% - 48px)'
  },
  readMore: {
    marginLeft: '.5rem',
    cursor: 'pointer'
  },
  messageDarkMode: {
    color: '#fff'
  },
  linkStyle: {
    '&:hover': {
      textDecoration: 'underline',
      cursor: 'pointer'
    }
  }
}))

const WithTitle: FC<{
  title: string
  message: string
  contentWrapper: string
  titleStyles: string
  messageStyles: string
  readMoreStyles: string
  readMoreMessage: string
  messageDarkMode: string
  linkStyle: string
  notificationKey: string
  variant?: VariantType
  link?: string
}> = ({
  title,
  message,
  contentWrapper,
  titleStyles,
  messageStyles,
  readMoreStyles,
  readMoreMessage,
  linkStyle,
  messageDarkMode,
  variant,
  link,
  notificationKey
}) => {
  const [readMore, setReadMore] = useState(false)
  const theme = useTheme()
  const navigate = useNavigate()
  const { closeSnackbar } = useSnackbar()
  const handleReadMore = () => {
    setReadMore(true)
  }
  const isDarkMode = localStorage.getItem(SELECTED_THEME) === 'dark'
  const handleNavigate = () => {
    if (link) {
      navigate(link)
      closeSnackbar(notificationKey)
    }
  }
  return (
    <Box className={contentWrapper} onClick={handleNavigate}>
      {title.length > 0 && (
        <Typography
          variant='subtitle1'
          className={`${titleStyles} ${link && message.length === 0 ? linkStyle : ''}`}
        >
          {title}
        </Typography>
      )}
      <Typography
        variant='caption'
        className={`${messageStyles} ${link && message.length > 0 ? linkStyle : ''}`}
        classes={{ root: isDarkMode ? messageDarkMode : '' }}
      >
        {message.length > 850 && !readMore ? `${message.slice(0, 850)}...` : message}
      </Typography>
      {message.length > 850 && !readMore && (
        <MuiLink
          onClick={() => handleReadMore()}
          underline='hover'
          className={readMoreStyles}
          sx={{ color: getIconColor(theme, variant) }}
        >
          {readMoreMessage}
        </MuiLink>
      )}
    </Box>
  )
}
const Notifier = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const classes = useStyles()
  const getBorderColor = (variant?: VariantType) => {
    switch (variant) {
      case 'success':
        return classes.sucessNotifier
      case 'error':
        return classes.errorNotifier
      case 'info':
        return classes.infoNotifier
      case 'warning':
        return classes.warningNotifier
      default:
        return classes.defaultNotifier
    }
  }
  const notifications: Notification[] = useSelector(
    (store: ApplicationState) => store.notifier.notifications || []
  )
  useHotkeys(`shift+${Key.Delete},shift+${Key.Backspace}`, () => {
    closeSnackbar()
  })
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const storeDisplayed = (id: string) => {
    displayed = [...displayed, id]
  }
  const removeDisplayed = (id: string | number) => {
    displayed = [...displayed.filter((key) => id !== key)]
  }
  useEffect(() => {
    notifications.forEach(({ key, title, message, data, options = {}, dismissed = false }) => {
      if (dismissed) {
        // dismiss snackbar using notistack
        closeSnackbar(key)
        return
      }

      // do nothing if snackbar is already displayed
      if (displayed.includes(key)) return

      const translatedMessage = message
        ? intl.formatMessage({ id: message, defaultMessage: message })
        : ''

      // display snackbar using notistack
      enqueueSnackbar(
        title ? (
          <WithTitle
            title={intl.formatMessage({ id: title, defaultMessage: title })}
            message={translatedMessage}
            contentWrapper={classes.contentWrapper}
            messageDarkMode={classes.messageDarkMode}
            titleStyles={classes.title}
            messageStyles={classes.message}
            readMoreStyles={classes.readMore}
            readMoreMessage={intl.formatMessage({ id: 'common.notifier.readMore' })}
            link={data?.link}
            linkStyle={classes.linkStyle}
            notificationKey={key}
          />
        ) : (
          <WithTitle
            title={''}
            message={translatedMessage}
            messageDarkMode={classes.messageDarkMode}
            contentWrapper={classes.contentWrapper}
            titleStyles={classes.title}
            messageStyles={classes.message}
            readMoreStyles={classes.readMore}
            readMoreMessage={intl.formatMessage({ id: 'common.notifier.readMore' })}
            link={data?.link}
            linkStyle={classes.linkStyle}
            notificationKey={key}
          />
        ),
        {
          key,
          ...options,
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          className: `${classes.notifier} ${getBorderColor(options.variant)}`,
          onClose: (event, reason, myKey) => {
            if (options.onClose) {
              options.onClose(event, reason, myKey)
            }
          },
          onExited: (event, myKey) => {
            // remove this snackbar from redux store
            dispatch(removeSnackbar(myKey))
            removeDisplayed(myKey)
          }
        }
      )

      // keep track of snackbars that we've displayed
      storeDisplayed(key)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    notifications,
    closeSnackbar,
    enqueueSnackbar,
    dispatch,
    intl,
    classes.notifier,
    classes.contentWrapper,
    classes.message,
    classes.title,
    classes.readMore
  ])

  return null
}

export default Notifier
